/*
 * Subscription purchase options — the approved PDP redesign (ClickUp 86d4a70cn).
 *
 * Loop mounts its widget client-side: the app block prints a skeleton and
 * loop_widget.js swaps in the real options some time later, then re-renders
 * them whenever the variant or the selected option changes. So this reads the
 * copy the section rendered into #subpo-config and decorates Loop's own nodes
 * rather than replacing them — Loop keeps owning the radios, the prices and
 * what gets posted to /cart/add.
 *
 * A MutationObserver does the waiting. The theme's older Loop tweaks guess with
 * setTimeout (2300ms, 3500ms in product-template.liquid); those fire before the
 * widget on a slow connection and after a visible flash on a fast one.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'subpo-config';
  var OPTION_SELECTOR = '[data-loop-widget-purchase-option]';
  var ONETIME_SELECTOR = '[data-loop-widget-onetime-purchase-option]';
  var SELECTED_CLASS = 'loop-widget-purchase-option-selected';

  var config = readConfig();
  if (!config) return;

  var container = null;
  var pending = false;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  function readConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function start() {
    container = document.querySelector('.loop-widget-container');
    if (!container) return;

    // Loop re-renders the whole option list on variant change, which drops
    // everything injected below, so the observer stays connected for the life
    // of the page rather than disconnecting on first success.
    var observer = new MutationObserver(schedule);
    observer.observe(container, { childList: true, subtree: true });

    container.addEventListener('click', schedule);
    schedule();
  }

  /*
   * Loop mutates in bursts; coalesce so one render is decorated once.
   *
   * setTimeout rather than requestAnimationFrame: a background tab never paints,
   * so rAF would not run — and if Loop finished mounting while the tab was
   * hidden there is no later mutation to retry on, leaving the shopper an
   * undecorated widget when they switch back to it.
   */
  function schedule() {
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      decorate();
    }, 0);
  }

  function decorate() {
    var options = container.querySelectorAll(OPTION_SELECTOR + ',' + ONETIME_SELECTOR);
    if (!options.length) return;

    container.classList.add('subpo');
    container.classList.add(config.kind === 'oneoff' ? 'subpo--oneoff' : 'subpo--plan');

    if (config.onetime && config.onetime.hidden) hideOnetime();

    renderLabel(options);
    var sale = currentSale();

    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      var isOnetime = option.matches(ONETIME_SELECTOR);
      var copy = isOnetime ? config.onetime : config.subscribe;
      if (!copy) continue;

      setLabel(option, copy.label);
      setBadge(option, !isOnetime && sale ? sale.badge : copy.badge);
      if (!isOnetime && sale) setCompare(option, sale.compare);

      if (!isOnetime && config.subscribe.ribbon) {
        addRibbon(option, config.subscribe.ribbon);
      }

      renderBody(option, copy, isOnetime);
    }

    renderDisclaimer();
    syncTopPrice();
    syncCtaLabel();
  }

  /*
   * Loop rewrites the theme's button to "Add subscription to cart" once a plan
   * is selected. product-form.liquid already prints the wording the store uses
   * ("Add subscription to bag") into data-default-text, so this restores that
   * exact string rather than editing Loop's words — the label then reads the
   * same before and after every redraw. It runs inside decorate, which the
   * observer re-runs after each of Loop's re-renders, so Loop does not get the
   * last word.
   *
   * Guarded on Loop's own wording: any other label on the button belongs to the
   * theme (sold out, pre-order) and is left alone.
   */
  function syncCtaLabel() {
    var button = document.getElementById('addToCartCTA');
    if (!button) return;
    var label = button.querySelector('[data-add-to-cart-text]') || button;
    if (!/subscription to cart/i.test(label.textContent)) return;
    var wanted = label.getAttribute('data-default-text');
    label.textContent = wanted || label.textContent.replace(/cart/i, 'bag');
  }

  /* ---------- pieces ---------- */

  /*
   * The plan control page stays subscription-only once a plan allows a one-time
   * buy (ClickUp 14zcqntjwar): the stylesheet hides Loop's one-time card from
   * first paint, and this covers browsers without :has(). If Loop ever starts
   * on the one-time card, Subscribe and Save is chosen instead, so a hidden
   * option is never the one that gets posted to the cart.
   */
  function hideOnetime() {
    var onetime = container.querySelector(ONETIME_SELECTOR);
    if (!onetime) return;
    onetime.style.setProperty('display', 'none', 'important');

    if (!onetime.classList.contains(SELECTED_CLASS)) return;
    // Loop gives the one-time card the general option attribute too, and draws
    // each card as a role="radio" div that takes the click itself.
    var subscribe = container.querySelector(OPTION_SELECTOR + ':not(' + ONETIME_SELECTOR + ')');
    if (subscribe) subscribe.click();
  }

  /*
   * Loop leaves the one-time card in the DOM and hides it with its own
   * loop-display-none while the product is subscription-only, so the plan PDP
   * paints a single card. A "Purchase Options:" heading over one card names a
   * choice the shopper hasn't got (Alarise, 09/18), so the label follows what
   * Loop actually shows rather than what it rendered. The previously released
   * PDP shows both cards and keeps the heading.
   */
  function renderLabel(options) {
    var existing = container.previousElementSibling;
    if (existing && !existing.classList.contains('subpo-label')) existing = null;

    if (!config.optionsLabel || countVisible(options) < 2) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;

    var label = document.createElement('p');
    label.className = 'subpo-label';
    label.textContent = config.optionsLabel;
    container.parentNode.insertBefore(label, container);
  }

  function countVisible(options) {
    var shown = 0;
    for (var i = 0; i < options.length; i++) {
      if (window.getComputedStyle(options[i]).display !== 'none') shown++;
    }
    return shown;
  }

  function setLabel(option, text) {
    if (!text) return;
    var label = option.querySelector('.loop-widget-purchase-option-label');
    if (label && label.textContent.trim() !== text) {
      label.textContent = text;
    }
  }

  // Loop prints a percentage badge ("15% OFF"); the approved copy is the money
  // saved, which the section worked out from the selling plan allocation.
  function setBadge(option, text) {
    var badge = option.querySelector('.loop-widget-purchase-option-discount-badge');
    if (!badge) return;
    if (!text) {
      badge.style.display = 'none';
      return;
    }
    badge.style.display = '';
    if (badge.textContent.trim() !== text) badge.textContent = text;
  }

  /*
   * On a variant that is on sale, Loop strikes through the sale price ($13.59)
   * over the subscription price. The section passes the regular price for each
   * such variant, so the card reads $15.99 struck over $10.99 (Joules, 10/08).
   * The variant comes from the form, which the theme updates on a switch.
   */
  function currentSale() {
    if (!config.subscribe || !config.subscribe.sale) return null;
    var form = container.closest('form') || document.querySelector('.product-single__form');
    var input = form && form.querySelector('[name="id"]');
    return input ? config.subscribe.sale[input.value] || null : null;
  }

  function setCompare(option, text) {
    var compare = option.querySelector('.loop-widget-purchase-option-compare-at-price');
    if (compare && compare.textContent.trim() !== text) compare.textContent = text;
  }

  function addRibbon(option, text) {
    option.classList.add('subpo-has-ribbon');
    if (option.querySelector('.subpo-ribbon')) return;
    var ribbon = document.createElement('span');
    ribbon.className = 'subpo-ribbon';
    ribbon.textContent = text;
    option.insertBefore(ribbon, option.firstChild);
  }

  /*
   * The plan PDP shows a perk list; the previously released PDP shows a lead
   * line and an inset perks panel. Both only make sense under the option the
   * shopper has actually chosen, which is how the mockups show them.
   */
  function renderBody(option, copy, isOnetime) {
    var existing = option.querySelector('.subpo-body');
    var selected = option.classList.contains(SELECTED_CLASS);

    // The one-time card on a previously released product carries no body at
    // all — its price is the whole message.
    var wanted = selected && (copy.note || (copy.perks && copy.perks.length));

    if (!wanted) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;

    var body = document.createElement('div');
    body.className = 'subpo-body';

    if (copy.note) {
      var note = document.createElement('p');
      note.className = 'subpo-note';
      note.textContent = copy.note;
      body.appendChild(note);
    }

    if (copy.perks && copy.perks.length) {
      var list = buildPerks(copy.perks);
      if (copy.perksTitle) {
        var box = document.createElement('div');
        box.className = 'subpo-perks-box';
        var title = document.createElement('p');
        title.className = 'subpo-perks-title';
        title.textContent = copy.perksTitle;
        box.appendChild(title);
        box.appendChild(list);
        body.appendChild(box);
      } else {
        body.appendChild(list);
      }
    }

    var header = option.querySelector('.loop-widget-purchase-option-header');
    if (header && header.nextSibling) {
      option.insertBefore(body, header.nextSibling);
    } else {
      option.appendChild(body);
    }

    // Nothing else reads this, but it keeps the one-time branch honest if the
    // plan products are ever opened up to a one-time buy.
    if (isOnetime) body.classList.add('subpo-body--onetime');
  }

  function buildPerks(perks) {
    var list = document.createElement('ul');
    list.className = 'subpo-perks';
    for (var i = 0; i < perks.length; i++) {
      var item = document.createElement('li');
      item.textContent = perks[i];
      list.appendChild(item);
    }
    return list;
  }

  /*
   * The price block at the top of the page prints the one-time price, so it
   * read $65.99 over a Subscribe and Save card saying $49.99 (Alarise, 9/11).
   * This copies whatever the selected option shows — its price, and its
   * compare-at struck through beside it — so the two always agree, including
   * after the shopper switches to One-Time Purchase.
   *
   * It runs on every decorate, which Loop's re-render on a variant change also
   * triggers, so the theme rewriting the price on that change does not stick.
   */
  function syncTopPrice() {
    var meta = container.closest('.product-single__meta');
    if (!meta) return;
    var price = meta.querySelector('[data-price-container] [data-product-price]');
    var selected = container.querySelector('.' + SELECTED_CLASS);
    if (!price || !selected) return;

    var now = textOf(selected, '.loop-widget-purchase-option-price');
    var was = textOf(selected, '.loop-widget-purchase-option-compare-at-price');
    if (!now) return;

    // Keep the .money span when there is one — currency tooling looks for it.
    var target = price.querySelector('.money') || price;
    if (target.textContent.trim() !== now) target.textContent = now;

    var block = price.parentNode;
    var compare = block.querySelector('.subpo-top-compare');
    var savings = block.querySelector('.subpo-top-savings');
    var a11yLabel = block.querySelector('[data-a11y-price]');
    var onSubscription = !selected.closest(ONETIME_SELECTOR);
    // Loop prints a compare-at on the one-time card too: equal to its price,
    // or the product's own compare-at when it is on sale. The theme already
    // shows that one, with its "% off" badge, so ours would only repeat it.
    var themeWrap = block.querySelector('[data-product-price-wrap]');
    var themeWas = themeWrap && !themeWrap.classList.contains('hide')
      ? textOf(themeWrap, '[data-compare-price]') : '';
    var showCompare = was && was !== now && (onSubscription || was !== themeWas);

    // With Subscribe and Save picked, the top reads like the theme's own sale
    // price ($15.99 struck, $10.99, "15% off") but with this option's numbers:
    // $15.99 struck, $10.99, and the card's "$5 OFF" (Joules, 10/08). The
    // theme's compare-at and badge describe the one-time sale, so they hide.
    block.classList.toggle('subpo-top-price--sub', !!showCompare);

    var badge = selected.querySelector('.loop-widget-purchase-option-discount-badge');
    var badgeText = onSubscription && showCompare && badge && badge.style.display !== 'none'
      ? badge.textContent.trim() : '';

    // The theme sizes a sale price through `on-sale` (a per-template setting),
    // so a product that is not on sale would show a smaller $10.99 beside the
    // struck price. Borrow the class while ours shows, and hand back only what
    // was borrowed: the theme sets it itself on a sale variant.
    if (showCompare && !price.classList.contains('on-sale')) {
      price.classList.add('on-sale');
      price.setAttribute('data-subpo-on-sale', '');
    } else if (!showCompare && price.hasAttribute('data-subpo-on-sale')) {
      // A variant switch onto a sale variant meanwhile makes it the theme's.
      if (!themeWas) price.classList.remove('on-sale');
      price.removeAttribute('data-subpo-on-sale');
    }

    if (!showCompare) {
      if (compare) compare.remove();
      if (savings) savings.remove();
      if (a11yLabel) a11yLabel.removeAttribute('aria-hidden');
      return;
    }

    // The theme's own classes, so it picks up the same size, weight and strike.
    if (!compare) {
      compare = document.createElement('span');
      compare.className = 'subpo-top-compare';
      compare.innerHTML =
        '<span class="visually-hidden">Regular price</span>' +
        '<span class="product__price product__price--compare"><span class="money"></span></span>' +
        '<span class="visually-hidden">Subscription price</span>';
      block.insertBefore(compare, a11yLabel && a11yLabel.parentNode === block ? a11yLabel : price);
    }
    var struck = compare.querySelector('.money');
    if (struck.textContent !== was) struck.textContent = was;

    if (!badgeText) {
      if (savings) savings.remove();
    } else {
      if (!savings) {
        savings = document.createElement('span');
        savings.className = 'product__price-savings subpo-top-savings';
      }
      if (savings.previousElementSibling !== price) block.insertBefore(savings, price.nextSibling);
      if (savings.textContent !== badgeText) savings.textContent = badgeText;
    }

    // The theme's own "Regular price" label would now announce the sale price.
    if (a11yLabel) a11yLabel.setAttribute('aria-hidden', 'true');
  }

  function textOf(root, selector) {
    var node = root.querySelector(selector);
    return node ? node.textContent.trim() : '';
  }

  /*
   * The disclaimer sits between the options and Add to Bag. It carries a couple
   * of bold spans, so it is the one string the section renders as HTML — built
   * from theme copy and prices, never from anything a shopper can supply.
   *
   * It only makes sense next to a Subscribe and Save card. When subscribing
   * costs more than the one-time price (a sale below the plan price), the theme
   * hides the whole widget, so the notice would warn about a choice the shopper
   * can't see (Roxanne, 10/08, ClickUp 14zcqntk09r). It follows what is shown,
   * so the hidden widget, a hidden card and a variant switch are all covered.
   */
  function renderDisclaimer() {
    var existing = container.parentNode.querySelector('.subpo-disclaimer');

    if (!config.disclaimer || !subscribeShown()) {
      if (existing) existing.remove();
      return;
    }

    if (existing) {
      if (existing.previousElementSibling !== container) {
        container.parentNode.insertBefore(existing, container.nextSibling);
      }
      return;
    }

    var wrapper = document.createElement('p');
    wrapper.className = 'subpo-disclaimer';
    wrapper.innerHTML =
      '<svg class="subpo-disclaimer-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
      '<circle cx="8" cy="4.6" r="0.9" fill="currentColor"/>' +
      '<path d="M8 7v5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
      '</svg><span class="subpo-disclaimer-text"></span>';
    wrapper.querySelector('.subpo-disclaimer-text').innerHTML = config.disclaimer;
    container.parentNode.insertBefore(wrapper, container.nextSibling);
  }

  // getClientRects is empty when the card or anything above it is display:none.
  function subscribeShown() {
    var cards = container.querySelectorAll(OPTION_SELECTOR + ':not(' + ONETIME_SELECTOR + ')');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getClientRects().length) return true;
    }
    return false;
  }
})();
